"use client";
import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState,
} from "react";
import { useInView } from "react-intersection-observer";
import { usePageVisible } from "@/app/hooks/usePageVisible";
import { useSharedInterval } from "@/app/hooks/useSharedInterval";

function cn(...classes: (string | undefined | null | boolean)[]): string {
  return classes.filter(Boolean).join(" ");
}

export interface RotatingTextRef {
  next: () => void;
  previous: () => void;
  jumpTo: (index: number) => void;
  reset: () => void;
}

export interface RotatingTextItem {
  text: string;
  icon?: React.ReactNode;
  color?: string;
  style?: React.CSSProperties;
}

export interface RotatingTextProps
  extends Omit<React.ComponentPropsWithoutRef<"div">, "children"> {
  items?: RotatingTextItem[];
  texts?: string[];
  className?: string;
  classNames?: string[];
  rotationInterval?: number;
  loop?: boolean;
  auto?: boolean;
  onNext?: (index: number) => void;
  mainClassName?: string;
}

/** Tek item: animasyon yok, zamanlayıcı yok, gözlemci yok — hiç hook çalışmaz. */
const StaticText = ({
  item,
  className,
  mainClassName,
  ...rest
}: {
  item: RotatingTextItem;
  className?: string;
  mainClassName?: string;
} & React.ComponentPropsWithoutRef<"div">) => (
  <div className={cn("relative overflow-hidden", mainClassName)} {...rest}>
    <div
      className={cn(
        "flex items-center gap-1 whitespace-nowrap font-medium",
        item.color || "",
        className,
      )}
      style={item.style}
    >
      {item.icon && <span className="shrink-0">{item.icon}</span>}
      <span dangerouslySetInnerHTML={{ __html: item.text }} />
    </div>
  </div>
);

interface AnimatedTextProps
  extends Omit<React.ComponentPropsWithoutRef<"div">, "children"> {
  displayItems: RotatingTextItem[];
  className?: string;
  rotationInterval: number;
  loop: boolean;
  auto: boolean;
  onNext?: (index: number) => void;
  mainClassName?: string;
}

const AnimatedText = forwardRef<RotatingTextRef, AnimatedTextProps>(
  (
    {
      displayItems,
      className,
      rotationInterval,
      loop,
      auto,
      onNext,
      mainClassName,
      ...rest
    },
    ref,
  ) => {
    const [currentIndex, setCurrentIndex] = useState<number>(0);
    // animKey: her index değişiminde farklı değer → CSS animation yeniden tetiklenir
    const [animKey, setAnimKey] = useState<number>(0);
    const isPageVisible = usePageVisible();

    // triggerOnce BİLEREK false: gözlemci sönerse ekran dışındaki her örnek
    // sonsuza dek dönmeye devam eder ve sayfa boştayken bile 2 saniyede bir
    // 168 reconciliation üretir. Kaydırma sırasındaki asıl maliyet zaten
    // gözlemci değil, örnek başına setInterval + document listener'ıydı;
    // ikisi de artık paylaşılıyor.
    const { ref: inViewRef, inView } = useInView({
      rootMargin: "200px 0px",
      triggerOnce: false,
    });

    const handleIndexChange = useCallback(
      (newIndex: number) => {
        setCurrentIndex(newIndex);
        setAnimKey((k) => k + 1);
        if (onNext) onNext(newIndex);
      },
      [onNext],
    );

    const next = useCallback(() => {
      const nextIndex =
        currentIndex === displayItems.length - 1
          ? loop
            ? 0
            : currentIndex
          : currentIndex + 1;
      if (nextIndex !== currentIndex) {
        handleIndexChange(nextIndex);
      }
    }, [currentIndex, displayItems.length, loop, handleIndexChange]);

    const previous = useCallback(() => {
      const prevIndex =
        currentIndex === 0
          ? loop
            ? displayItems.length - 1
            : currentIndex
          : currentIndex - 1;
      if (prevIndex !== currentIndex) {
        handleIndexChange(prevIndex);
      }
    }, [currentIndex, displayItems.length, loop, handleIndexChange]);

    const jumpTo = useCallback(
      (index: number) => {
        const validIndex = Math.max(
          0,
          Math.min(index, displayItems.length - 1),
        );
        if (validIndex !== currentIndex) {
          handleIndexChange(validIndex);
        }
      },
      [displayItems.length, currentIndex, handleIndexChange],
    );

    const reset = useCallback(() => {
      if (currentIndex !== 0) {
        handleIndexChange(0);
      }
    }, [currentIndex, handleIndexChange]);

    useImperativeHandle(ref, () => ({ next, previous, jumpTo, reset }), [
      next,
      previous,
      jumpTo,
      reset,
    ]);

    // Örnek başına setInterval yerine periyot başına TEK zamanlayıcı: tüm
    // rotasyonlar aynı karede döner, React tek batch'te işler.
    useSharedInterval(next, rotationInterval, auto && inView && isPageVisible);

    const currentItem = displayItems[currentIndex];
    if (!currentItem) return null;

    return (
      <div
        ref={inViewRef}
        className={cn("relative overflow-hidden", mainClassName)}
        {...rest}
      >
        {/*
          animKey key prop'u her geçişte değişir → React yeni div mount eder →
          CSS animation @keyframes rotate-in yeniden tetiklenir (alttan gelir).
          spring benzeri: cubic-bezier(0.34, 1.56, 0.64, 1) ~= damping:25 stiffness:300
        */}
        <div
          key={animKey}
          className={cn(
            "flex items-center gap-1 whitespace-nowrap font-medium",
            currentItem.color || "",
            className,
          )}
          style={{
            ...currentItem.style,
            animation: "rotate-in 0.35s cubic-bezier(0.34, 1.56, 0.64, 1) both",
          }}
        >
          {currentItem.icon && (
            <span className="shrink-0">{currentItem.icon}</span>
          )}
          <span dangerouslySetInnerHTML={{ __html: currentItem.text }} />
        </div>
      </div>
    );
  },
);

AnimatedText.displayName = "AnimatedText";

/**
 * Dış bileşen — HİÇ hook çağırmıyor. Tek/sıfır item'lı listelerde (kartların
 * çoğu) IntersectionObserver bile kurulmuyor; eskiden useInView hook sırası
 * yüzünden statik render'da da gözlemci kuruluyordu.
 */
const RotatingText = forwardRef<RotatingTextRef, RotatingTextProps>(
  (
    {
      items = [],
      texts = [],
      classNames = [],
      className,
      rotationInterval = 2000,
      loop = true,
      auto = true,
      onNext,
      mainClassName,
      ...rest
    },
    ref,
  ) => {
    const displayItems: RotatingTextItem[] =
      items.length > 0
        ? items
        : texts.map((text, index) => ({
            text,
            color: classNames[index] || "",
          }));

    if (displayItems.length === 0) return null;

    if (displayItems.length === 1) {
      return (
        <StaticText
          item={displayItems[0]}
          className={className}
          mainClassName={mainClassName}
          {...rest}
        />
      );
    }

    return (
      <AnimatedText
        ref={ref}
        displayItems={displayItems}
        className={className}
        rotationInterval={rotationInterval}
        loop={loop}
        auto={auto}
        onNext={onNext}
        mainClassName={mainClassName}
        {...rest}
      />
    );
  },
);

RotatingText.displayName = "RotatingText";
export default RotatingText;
